import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  QuizSession,
  QuizModeProvider,
  QuizStyleProvider,
  type QuizScript,
  type QuizResponseData,
  type QuizResponseResult,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { learnerDiagnosticsApi } from '@/api/learnerDiagnostics'
import { useCompleteDiagnosticAiq } from '@/hooks/learner/useDiagnosticMutations'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { TestLayout } from '@/components/learner/test'
import { Button } from '@/components/learner/ui/Button'

export default function DiagnosticAiq() {
  const { diagnosticId = '' } = useParams<{ diagnosticId: string }>()
  const navigate = useNavigate()
  const complete = useCompleteDiagnosticAiq()
  const { t } = useTranslation('learner')
  const [started, setStarted] = useState(false)
  const [quizList, setQuizList] = useState<QuizScript[] | null>(null)
  const [initialIndex, setInitialIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // AIQ 시작(멱등) → 확정 순서 + 이어풀기 지점 → 스크립트 로드(showAnswer=false, useFixedVars=true).
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const start = await learnerDiagnosticsApi.startAiq()
        const { quizIds, nextSeq } = start.data.data
        if (quizIds.length === 0) { if (active) setError('AIQ 문항이 없습니다.'); return }
        const fetched = await quizScriptApi.getQuizzes(quizIds, false, true, false)
        const byId = new Map(fetched.map((q) => [String(q.quizId), q]))
        const ordered = quizIds.map((id) => byId.get(String(id))).filter(Boolean) as QuizScript[]
        if (!active) return
        if (ordered.length === 0) { setError('AIQ 문항을 불러오지 못했습니다.'); return }
        setInitialIndex(Math.min(nextSeq, ordered.length - 1))
        setQuizList(ordered)
      } catch {
        if (active) setError('AIQ를 불러오지 못했습니다.')
      }
    })()
    return () => { active = false }
  }, [])

  const onQuizResponse = useCallback(
    async (data: QuizResponseData): Promise<QuizResponseResult> => {
      // spec §9.1: 학습자가 본 vars를 echo해야 backend 재채점이 일관됨(미echo 시 오채점).
      // QuizContainer가 onQuizResponse payload에 quiz.vars를 실어 보낸다(Task 9).
      const res = await learnerDiagnosticsApi.submitResponse({
        quizId: data.quizId,
        answers: data.answers,
        vars: (data.vars ?? {}) as Record<string, unknown>,
        elapsedSec: data.timeSpentSec ?? 0,
        elapsedMs: data.elapsedMs,
      })
      const d = res.data.data
      return {
        isCorrect: d.isCorrect,
        corrects: (d.corrects ?? []).map((c) => c ?? false),
        correctAnswers: d.correctAnswers,
      }
    },
    [],
  )

  const onComplete = useCallback(async () => {
    await complete.mutateAsync()
    navigate(`/my/diagnostic/${diagnosticId}/aiq-complete`, { replace: true })
  }, [complete, navigate, diagnosticId])

  const sessionId = useMemo(() => Number(diagnosticId), [diagnosticId])

  if (error) {
    return <main className="min-h-screen flex items-center justify-center text-lc-gray-700">{error}</main>
  }
  if (!quizList) {
    return <LearnerLoading />
  }
  // 축2(표시 전용): 시작 전 안내 — 총 문항 수만 표시, 권장시간은 Phase 2 연기(엣지9).
  if (!started) {
    return (
      <TestLayout variant="card">
        <div className="flex flex-col items-center gap-12">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-2">
              <p className="text-lc-v3-h4 font-semibold text-lc-text-link">
                {t('diagnostic.start.eyebrow')}
              </p>
              <h1 className="text-lc-v3-h1 font-bold text-lc-text-primary">
                {t('diagnostic.start.title')}
              </h1>
            </div>
            <p className="text-lc-v3-body-1 font-medium text-lc-text-secondary">
              {t('diagnostic.start.description')}
            </p>
            <div className="inline-flex items-center justify-center rounded-full bg-lc-key-50 px-3.5 py-2 text-lc-v3-label-lg font-medium text-lc-key-500">
              {t('diagnostic.start.count', { total: quizList.length })}
            </div>
            {initialIndex > 0 ? (
              <p className="text-lc-v3-body-1 font-medium text-lc-text-secondary">
                {t('diagnostic.start.resume', { solved: initialIndex })}
              </p>
            ) : null}
          </div>
          <Button
            size="XL"
            onClick={() => setStarted(true)}
            className="px-10"
          >
            {initialIndex > 0 ? t('diagnostic.start.ctaResume') : t('diagnostic.start.cta')}
          </Button>
        </div>
      </TestLayout>
    )
  }
  return (
    <QuizModeProvider value={{ mode: 'student' }}>
      <QuizStyleProvider variant="aiq">
        <QuizSession
          contentId={0}
          quizList={quizList}
          quizScriptApi={quizScriptApi}
          assessmentMode
          sessionId={sessionId}
          initialIndex={initialIndex}
          onQuizResponse={onQuizResponse}
          onComplete={onComplete}
        />
      </QuizStyleProvider>
    </QuizModeProvider>
  )
}
